import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChefHat,
  Loader2,
  QrCode,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

const MAIN_IMAGE =
  "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1600&q=85";

const FOOD_IMAGE =
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=700&q=85";

const DISH_IMAGE =
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=700&q=85";

const PLANS = {
  basic: {
    id: "basic",
    name: "Basic",
    price: 699,
    audience: "For single-outlet restaurants",
    features: [
      "POS & Billing",
      "Table & Menu Management",
      "KDS",
      "Customer & Staff Management",
      "Basic Inventory",
      "Expenses & Reports",
      "QR Menu / QR Ordering",
      "GST Billing",
    ],
  },

  starter: {
    id: "starter",
    name: "Starter",
    price: 199,
    audience: "For restaurants that need simple POS & menu management",
    features: ["POS & Billing", "Menu Management", "Basic Dashboard"],
  },
};

const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Razorpay SDK failed to load"));

    document.body.appendChild(script);
  });

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedPlanId =
    searchParams.get("plan") === "starter" ? "starter" : "basic";

  const [selectedPlan, setSelectedPlan] = useState(PLANS[selectedPlanId]);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    restaurant_name: "",
    owner_email: "",
    phone: "",
    referral_code: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    api
      .publicPlans()
      .then((response) => {
        const apiPlans = Array.isArray(response.data) ? response.data : [];

        const apiPlan = apiPlans.find(
          (plan) => String(plan.id || "").toLowerCase() === selectedPlanId,
        );

        if (apiPlan) {
          setSelectedPlan((current) => ({
            ...current,
            id: apiPlan.id || current.id,
            name: apiPlan.name || current.name,
            price: apiPlan.price ?? current.price,
            features: Array.isArray(apiPlan.features)
              ? apiPlan.features
              : current.features,
          }));
        }
      })
      .catch(() => {
        // Keep the local plan configuration if API is unavailable.
      });
  }, [selectedPlanId]);

  const change = (key, value) => {
    if (key === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setForm((current) => ({
      ...current,
      [key]: value,
    }));

    setErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const next = {};

    if (!form.restaurant_name.trim()) {
      next.restaurant_name = "Restaurant name is required";
    }

    if (!form.owner_email.trim()) {
      next.owner_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)) {
      next.owner_email = "Invalid email address";
    }

    if (!form.phone.trim()) {
      next.phone = "Phone is required";
    } else if (!/^[6-9]\d{9}$/.test(form.phone)) {
      next.phone = "Enter a valid 10-digit mobile number";
    }

    if (!form.password) {
      next.password = "Password is required";
    } else if (form.password.length < 6) {
      next.password = "Password must be at least 6 characters";
    }

    if (!form.confirm_password) {
      next.confirm_password = "Please confirm your password";
    } else if (form.password !== form.confirm_password) {
      next.confirm_password = "Passwords do not match";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSignup = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    setBusy(true);

    try {
      /*
       * Basic is intentionally hard-coded for the public launch.
       * The UI does not expose another plan and the request always sends
       * plan=basic.
       *
       * owner_name is kept only for backend compatibility. Since the UI no
       * longer asks for an admin/owner name, the restaurant name is used as
       * the initial owner display name.
       */
      const signupRes = await api.publicSignup({
        restaurant_name: form.restaurant_name.trim(),
        owner_name: form.restaurant_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        phone: form.phone.trim(),
        referral_code: form.referral_code.trim(),
        password: form.password,
        confirm_password: form.confirm_password,
        plan: selectedPlanId,
      });

      const { restaurant_id, restaurant_name, plan } = signupRes.data;

      const paymentRes = await api.createSignupPayment(restaurant_id);

      const { key_id, subscription_id, trial_amount, recurring_amount } =
        paymentRes.data;

      await loadRazorpay();

      const options = {
        key: key_id,

        subscription_id,

        name: "StayServe",

        description:
          Number(trial_amount) > 0
            ? `₹${trial_amount} activation • ₹${recurring_amount}/month after 7 days`
            : `₹${recurring_amount}/month subscription`,

        prefill: {
          name: form.restaurant_name.trim(),
          email: form.owner_email.trim().toLowerCase(),
          contact: form.phone.trim(),
        },

        theme: {
          color: "#f97316",
        },

        notes: {
          restaurant_name,
          plan: plan || "basic",
          type: "stayserve_subscription",
          trial_amount: String(trial_amount),
          recurring_amount: String(recurring_amount),
        },

        handler: async (response) => {
          try {
            setBusy(true);

            await api.verifySignupPayment(restaurant_id, {
              razorpay_subscription_id: response.razorpay_subscription_id,

              razorpay_payment_id: response.razorpay_payment_id,

              razorpay_signature: response.razorpay_signature,
            });

            setStep("done");

            toast.success(
              Number(trial_amount) > 0
                ? "Payment successful! Your 7-day trial has started."
                : "Payment successful! Your subscription is active.",
            );
          } catch (error) {
            toast.error(
              error.response?.data?.detail ||
                "Payment verification failed. Please contact support.",
            );
          } finally {
            setBusy(false);
          }
        },

        modal: {
          ondismiss: () => {
            setBusy(false);

            toast.error(
              "Payment cancelled. Your account is saved — you can retry payment anytime.",
            );
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {
        setBusy(false);

        toast.error(
          response.error?.description || "Payment failed. Please try again.",
        );
      });

      razorpay.open();
    } catch (error) {
      const message =
        error.response?.data?.detail || "Signup failed. Please try again.";

      toast.error(message);

      if (message.toLowerCase().includes("email")) {
        setErrors((current) => ({
          ...current,
          owner_email: message,
        }));
      }
    } finally {
      setBusy(false);
    }
  };

  if (step === "done") {
    return (
      <div className="min-h-screen bg-[#0b0b0b] flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-[28px] bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 size={32} />
          </div>

          <h2 className="font-display text-2xl font-black text-stone-950">
            You&apos;re all set!
          </h2>

          <p className="mt-3 text-sm leading-6 text-stone-500">
            Your StayServe account is active. Sign in with your email and
            password to get started.
          </p>

          <p className="mt-2 text-xs text-stone-400">
            Email: <strong>{form.owner_email}</strong>
          </p>

          <Button
            onClick={() => navigate("/login")}
            className="mt-6 h-12 w-full rounded-xl bg-orange-500 font-bold text-white hover:bg-orange-600"
          >
            Go to Login
            <ArrowRight size={16} className="ml-2" />
          </Button>

          <p className="mt-4 text-xs text-stone-400">
            Need help? Contact{" "}
            <a
              href="https://ssquarelogic.com"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-stone-600 hover:text-orange-500"
            >
              SSQUARE LOGIC
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#0b0b0b] text-white">
      <style>{`
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }

        @keyframes floatReverse {
          0%, 100% { transform: translateY(-4px); }
          50% { transform: translateY(10px); }
        }

        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 rgba(249, 115, 22, 0); }
          50% { box-shadow: 0 0 35px rgba(249, 115, 22, 0.22); }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }

        .signup-float {
          animation: floatSlow 5s ease-in-out infinite;
        }

        .signup-float-reverse {
          animation: floatReverse 6s ease-in-out infinite;
        }

        .signup-glow {
          animation: pulseGlow 4s ease-in-out infinite;
        }

        .signup-slide {
          animation: slideUp 0.7s ease-out both;
        }

        .signup-shimmer {
          position: relative;
          overflow: hidden;
        }

        .signup-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          width: 40%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255,255,255,0.10),
            transparent
          );
          transform: translateX(-120%);
          animation: shimmer 4s ease-in-out infinite;
        }
      `}</style>

      <div className="grid min-h-screen lg:grid-cols-[1.08fr_0.92fr]">
        {/* =========================================================
            LEFT — SAME VISUAL LANGUAGE AS LOGIN
        ========================================================= */}
        <section className="relative hidden overflow-hidden lg:block">
          <img
            src={MAIN_IMAGE}
            alt="Restaurant kitchen"
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/35 to-orange-950/60" />

          <div className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-orange-600/20 blur-[110px]" />
          <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-orange-500/10 blur-[120px]" />

          <div className="relative z-10 flex min-h-screen flex-col justify-between p-10 xl:p-14">
            <a
              href="/"
              className="flex items-center gap-3 px-3 py-1 rounded-xl w-fit cursor-pointer"
            >
              <div className="flex items-center gap-3  px-3 py-1 rounded-xl w-fit">
                <img
                  src="/assets/logo-white.png"
                  alt="StayServe"
                  className="h-12 w-auto object-contain"
                />

                <div>
                  <div className="font-display text-[18px] font-black tracking-tight leading-none">
                    <span className="text-[#ffffff]">Stay</span>
                    <span className="text-[#FF6B00]">Serve</span>
                  </div>

                  <div className="mt-1 text-[5px] font-bold uppercase tracking-[.20em] text-[#ffffff]">
                    Hospitality operations
                  </div>
                </div>
              </div>
            </a>

            <div className="relative mt-4 max-w-2xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md">
                <span className="h-2 w-2 rounded-full bg-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.9)]" />
                <span className="text-xs font-medium text-stone-300">
                  Start your StayServe workspace
                </span>
              </div>

              <h1 className="font-display text-5xl font-black leading-[0.98] tracking-[-0.035em] text-white xl:text-6xl">
                Your hospitality
                <br />
                business, ready from
                <br />
                <span className="text-orange-500">one screen.</span>
              </h1>

              <p className="mt-6 max-w-xl text-base leading-7 text-stone-300 xl:text-lg">
                Set up your restaurant, connect your operations and start
                running orders, kitchen, billing, inventory and analytics in one
                powerful platform.
              </p>

              <div className="mt-7 flex flex-wrap gap-2">
                {[
                  [QrCode, "QR Ordering"],
                  [ChefHat, "Kitchen"],
                  [BarChart3, "Analytics"],
                  [ShieldCheck, "Secure"],
                ].map(([Icon, text]) => (
                  <div
                    key={text}
                    className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3.5 py-2 text-xs text-stone-300 backdrop-blur-md"
                  >
                    <Icon size={14} className="text-orange-400" />
                    {text}
                  </div>
                ))}
              </div>

              <div className="pointer-events-none absolute -right-10 -top-24 hidden xl:block">
                <div className="signup-float rounded-2xl border border-white/15 bg-black/45 p-2 shadow-2xl backdrop-blur-xl">
                  <img
                    src={FOOD_IMAGE}
                    alt="StayServe restaurant order preview"
                    className="h-36 w-44 rounded-xl object-cover"
                  />

                  <div className="flex items-center justify-between px-2 py-2">
                    <div>
                      <div className="text-[11px] font-bold">Order #1048</div>
                      <div className="text-[9px] text-stone-400">Preparing</div>
                    </div>

                    <div className="rounded-full bg-orange-500/20 px-2 py-1 text-[9px] font-bold text-orange-400">
                      LIVE
                    </div>
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute -right-4 top-36 hidden xl:block">
                <div className="signup-float-reverse flex items-center gap-3 rounded-2xl border border-white/10 bg-black/50 p-3 shadow-2xl backdrop-blur-xl">
                  <img
                    src={DISH_IMAGE}
                    alt="StayServe QR order preview"
                    className="h-14 w-14 rounded-xl object-cover"
                  />

                  <div>
                    <div className="text-[10px] text-stone-400">
                      New QR Order
                    </div>
                    <div className="mt-0.5 text-sm font-bold">Table 07</div>
                    <div className="mt-1 flex items-center gap-1 text-[9px] text-orange-400">
                      <CheckCircle2 size={10} />
                      Sent to kitchen
                    </div>
                  </div>
                </div>
              </div>

              <div className="mx-auto mt-10 grid w-full max-w-xl grid-cols-3 gap-5 border-t border-white/10 pt-7">
                <div>
                  <div className="font-display text-2xl font-black text-orange-400">
                    ₹99
                  </div>
                  <div className="mt-1 text-xs text-stone-500">Trial start</div>
                </div>

                <div>
                  <div className="font-display text-2xl font-black text-orange-400">
                    ₹{selectedPlan.price}
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    {selectedPlan.name} / month
                  </div>
                </div>

                <div>
                  <div className="font-display text-2xl font-black text-orange-400">
                    2
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    Plans at launch
                  </div>
                </div>
              </div>
              <div className="mx-auto mt-8 w-full max-w-xl rounded-[24px] border border-orange-500/40 bg-orange-500/[.06] p-5 shadow-[0_18px_50px_rgba(0,0,0,.18)] sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-display text-xl font-black text-white">
                        {selectedPlan.name}
                      </p>

                      <span className="rounded-full bg-orange-500 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[.12em] text-white">
                        Available now
                      </span>
                    </div>

                    <p className="mt-2 text-sm text-stone-400">
                      {selectedPlan.audience}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="font-display text-3xl font-black text-orange-400">
                      ₹{selectedPlan.price}
                    </p>
                    <span className="text-xs text-stone-500">/ month</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-[10px] leading-5 text-stone-400 text-white">
              <div>
                Designed &amp; developed by{" "}
                <a
                  href="https://ssquarelogic.com"
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-stone-600 text-white hover:text-orange-500"
                >
                  SSQUARE LOGIC
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            RIGHT — LOGIN-STYLE SIGNUP FORM
        ========================================================= */}
        <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#faf9f7] px-3 py-8 text-stone-900 sm:px-5 sm:py-10">
          <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-orange-200/40 blur-3xl lg:hidden" />
          <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-orange-100/50 blur-3xl lg:hidden" />

          <div className="relative z-10 w-full max-w-lg signup-slide">
            {/* Mobile header */}
            <div className="mb-8 -mx-5 flex items-center justify-between border-b border-white/10 bg-[#0b0b0b] px-5 py-4 sm:-mx-10 sm:px-10 lg:hidden">
              <Link to="/" className="flex items-center">
                <img
                  src="/assets/logo-white.png"
                  alt="StayServe"
                  className="h-10 w-auto object-contain"
                />
              </Link>

              <Link
                to="/login"
                className="text-xs font-semibold text-stone-400 transition hover:text-white"
              >
                Already have an account?{" "}
                <span className="text-orange-400">Sign in</span>
              </Link>
            </div>

            {/* Desktop top link */}
            <div className="mb-7 hidden items-center justify-end lg:flex">
              <Link
                to="/login"
                className="text-sm text-stone-500 transition hover:text-stone-900"
              >
                Already have an account?{" "}
                <span className="font-semibold text-orange-500">Sign in</span>
              </Link>
            </div>

            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-orange-600">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                Get started
              </div>

              <h2 className="font-display text-4xl font-black tracking-[-0.03em] text-stone-950 sm:text-[42px]">
                Create your account.
              </h2>

              <p className="mt-2 text-sm leading-6 text-stone-500">
                Set up your restaurant and start with the StayServe{" "}
                {selectedPlan.name} plan.
              </p>

              <div className="mt-4 flex items-center justify-between rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-orange-600">
                    Selected plan
                  </p>
                  <p className="mt-1 text-sm font-bold text-stone-900">
                    {selectedPlan.name}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-display text-2xl font-black text-orange-500">
                    ₹{selectedPlan.price}
                  </p>
                  <p className="text-[10px] text-stone-500">per month</p>
                </div>
              </div>
            </div>

            <div className="signup-glow mt-7 rounded-[28px] border border-stone-200 bg-white p-6 shadow-[0_20px_70px_rgba(0,0,0,0.08)] sm:p-7">
              <form onSubmit={handleSignup} className="space-y-5" noValidate>
                <Field
                  label="Restaurant name"
                  id="restaurant_name"
                  value={form.restaurant_name}
                  onChange={(value) => change("restaurant_name", value)}
                  error={errors.restaurant_name}
                  placeholder="e.g. Spice Garden"
                  autoComplete="organization"
                />

                <Field
                  label="Email address"
                  id="owner_email"
                  type="email"
                  value={form.owner_email}
                  onChange={(value) => change("owner_email", value)}
                  error={errors.owner_email}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Mobile number"
                    id="phone"
                    value={form.phone}
                    onChange={(value) => change("phone", value)}
                    error={errors.phone}
                    placeholder="+91 98765 43210"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                  <Field
                    label="Referral code (Optional)"
                    id="referral_code"
                    value={form.referral_code}
                    onChange={(value) => change("referral_code", value)}
                    placeholder="Enter referral code"
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Password"
                    id="password"
                    type="password"
                    value={form.password}
                    onChange={(value) => change("password", value)}
                    error={errors.password}
                    placeholder="Min 6 characters"
                    autoComplete="new-password"
                  />

                  <Field
                    label="Confirm password"
                    id="confirm_password"
                    type="password"
                    value={form.confirm_password}
                    onChange={(value) => change("confirm_password", value)}
                    error={errors.confirm_password}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                  />
                </div>
                {errors.plan && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {errors.plan}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  className="signup-shimmer relative h-12 w-full rounded-xl bg-orange-500 text-[14px] font-bold text-white shadow-[0_8px_25px_rgba(249,115,22,0.28)] transition-all hover:bg-orange-600 hover:shadow-[0_12px_30px_rgba(249,115,22,0.35)]"
                >
                  {busy ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      <span className="ml-2">Processing...</span>
                    </>
                  ) : (
                    <>
                      {selectedPlanId === "basic"
                        ? "Start ₹99 Trial"
                        : "Continue to Payment"}
                      <ArrowRight size={17} className="ml-2" />
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-[10px] font-medium text-stone-400">
                  <ShieldCheck size={13} className="text-green-500" />
                  Secure payment powered by Razorpay
                </div>

                <p className="text-center text-[10px] leading-5 text-stone-400">
                  {selectedPlanId === "basic"
                    ? "₹99 activation today • 7-day trial • ₹699/month after trial"
                    : "₹199/month Starter plan • secure subscription checkout"}
                </p>

                <p className="text-center text-[10px] leading-5 text-stone-400">
                  By continuing, you agree to our{" "}
                  <Link
                    to="/terms-and-conditions"
                    className="font-medium text-stone-600 hover:text-orange-500"
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy-policy"
                    className="font-medium text-stone-600 hover:text-orange-500"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({
  label,
  id,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
  autoComplete,
}) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500"
      >
        {label}
      </Label>

      <Input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-2 h-12 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-sm transition-all placeholder:text-stone-400 focus:border-orange-400 focus:bg-white focus:ring-orange-100"
      />

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
